import React from "react";
import { observer } from "mobx-react";
import { action, computed, observable, reaction } from "mobx";
import { fuzzyFilter } from "../Fuzzy";
import classNames from "classnames";
import { ConnectionWord, DataSourceWord, EnvironmentWord, IndexWord, OutputWord } from "../Language";
import { scrollIntoView } from "../Utils";
import groupBy from "lodash/groupBy";
import concat from "lodash/concat";
import { statusBadge } from "../../templates/common/TemplateStateBadge";
import { RouteComponentProps, withRouter } from "react-router-dom";
import { AppContext } from "../../routes/AppContext";
import "../../styles/omni-search.scss";
import Dialog from "@mui/material/Dialog";
import { AutocompleteValue } from "../api/contracts/AutoComplete";
import { getItemUriByItemType, highlightKey, isUUID } from "../omni";
import useLoginState from "../api/useLoginState";
import { useLoginStateProvider } from "../../LoginStateProvider";

export const itemTypeToWord: Record<string, React.ReactNode> = {
  InputItemType: <DataSourceWord.component single />,
  IndexItemType: <IndexWord.component single />,
  OutputItemType: <OutputWord.component single />,
  EnvironmentItemType: <EnvironmentWord.component single />,
  ConnectionItemType: <ConnectionWord.component single />,
  list: <span>Go to page</span>,
  impersonate: <span>Impersonate</span>,
};

@observer
class OmniItem extends React.Component<{ item: AutocompleteValue; highlight?: boolean }> {
  _ref: HTMLElement;

  componentDidUpdate() {
    if (this._ref && this.props.highlight) {
      scrollIntoView(this._ref);
    }
  }

  render() {
    const { item, highlight } = this.props;
    return (
      <li ref={(r) => (this._ref = r)} onMouseUp={item.select} className={classNames({ highlight })}>
        <span title={item.displayValue}>{item.displayValue}</span>
        {item.status && statusBadge[item.status.clazz]}
        <span className="word">{itemTypeToWord[item.itemType.clazz]}</span>
      </li>
    );
  }
}

const OmniItems = ({ items, selected }: { items: Array<AutocompleteValue>; selected: string }) => (
  <ul className="no-style-list">
    {items.map((x) => (
      <OmniItem item={x} key={x.highlightKey} highlight={selected && selected === x.highlightKey} />
    ))}
  </ul>
);

const systemOmniItems = (push: (path: string) => void) => [
  {
    id: "inputs-list",
    highlightKey: "inputs-list",
    displayValue: DataSourceWord.plural(),
    select: () => push("/inputs/list"),
    itemType: { clazz: "list" },
  },
  {
    id: "indexes-list",
    highlightKey: "indexes-list",
    displayValue: IndexWord.plural(),
    select: () => push("/indexes/list"),
    itemType: { clazz: "list" },
  },
  {
    id: "outputs-list",
    highlightKey: "outputs-list",
    displayValue: OutputWord.plural(),
    select: () => push("/outputs/list"),
    itemType: { clazz: "list" },
  },
  {
    id: "envs-list",
    highlightKey: "envs-list",
    displayValue: EnvironmentWord.plural(),
    select: () => push("/environments/list"),
    itemType: { clazz: "list" },
  },
  {
    id: "aliases-list",
    highlightKey: "aliases-list",
    displayValue: "Aliases",
    select: () => push("/indexes/aliases"),
    itemType: { clazz: "list" },
  },
  {
    id: "connections-list",
    highlightKey: "connections-list",
    displayValue: "Connections",
    select: () => push("/connections"),
    itemType: { clazz: "list" },
  },
  {
    id: "credits-list",
    highlightKey: "credits-list",
    displayValue: "Credits",
    select: () => push("/account/credits"),
    itemType: { clazz: "list" },
  },
];

@observer
class OmniSearch extends React.Component<RouteComponentProps & { impersonate: (orgId: string) => Promise<any> }> {
  static contextType = AppContext;
  context!: React.ContextType<typeof AppContext>;
  @observable _selectedItemIndex: number = 0;
  @observable _filter: string = "";
  @observable.ref _items: Array<AutocompleteValue>;
  _dispose: () => void;

  componentDidMount() {
    window.addEventListener("keyup", this._listener);
    window.addEventListener("keydown", this._listener);
    this._dispose = reaction(
      () => this.context.api.omniSearch,
      (show) => {
        if (show) {
          this._load();
        }
      }
    );
    this._load();
  }

  _load = action(() => {
    this._items = null;
    this._filter = "";
    this._selectedItemIndex = 0;
    this.context.opsInspection.omniAutocompleteItems().then(
      action((x: AutocompleteValue[]) => {
        const groups = groupBy(x, "itemType.clazz");
        if (groups["OrganizationItemType"]) {
          groups["OrganizationItemType"].forEach((y: any) => {
            y.highlightKey = `org-${y.id}`;
            y.idParts = y.id.split("-");
            y.select = () => this.props.impersonate(y.id).then(() => this._close());
            y.itemType = { clazz: "impersonate" };
          });
        }
        Object.keys(groups)
          .filter((k) => k !== "OrganizationItemType")
          .forEach((k) => {
            groups[k].forEach((y: any) => {
              y.highlightKey = highlightKey(y);
              y.idParts = y.id.split("-");
              y.select = () => this._select(y.highlightKey);
            });
          });
        const system = systemOmniItems((path) => {
          this.props.history.push(path);
          this._close();
        });
        system.push({
          id: "unimpersonate",
          highlightKey: "unimpersonate",
          displayValue: "unimpersonate",
          select: () => {
            this.context.api.clearImpersonate();
            window.location.reload();
          },
          itemType: { clazz: "impersonate" },
        });
        this._items = concat(
          groups["InputItemType"],
          groups["OutputItemType"],
          groups["IndexItemType"],
          groups["EnvironmentItemType"],
          system,
          groups["OrganizationItemType"]
        ).filter((x) => x);
      })
    );
  });

  componentWillUnmount() {
    window.removeEventListener("keyup", this._listener);
    window.removeEventListener("keydown", this._listener);
    if (this._dispose) {
      this._dispose();
    }
  }

  _listener = action((e: KeyboardEvent) => {
    if (this.context.api.omniSearch && this._filteredItems) {
      if (e.type === "keydown") {
        if (e.key === "ArrowUp") {
          if (this._selectedItemIndex - 1 < 0) {
            this._selectedItemIndex = this._filteredItems.length - 1;
          } else {
            this._selectedItemIndex--;
          }
        } else if (e.key === "ArrowDown") {
          if (this._selectedItemIndex + 1 < this._filteredItems.length) {
            this._selectedItemIndex++;
          } else {
            this._selectedItemIndex = 0;
          }
        }
      } else if (e.type === "keyup") {
        if (e.key === "Enter") {
          const idx =
            this._selectedItemIndex > this._filteredItems.length || this._filteredItems.length === 1
              ? 0
              : this._selectedItemIndex;
          if (this._filteredItems && this._filteredItems.length > idx) {
            this._filteredItems[idx].select(null);
            this._close();
          }
        } else if (e.key === "Escape") {
          this._close();
        }
      }
    }
  });

  _close = action(() => (this.context.api.omniSearch = false));

  _select = action((highlightKey: string) => {
    this._selectedItemIndex = this._filteredItems.findIndex((x) => x.highlightKey === highlightKey);
    this.props.history.push(getItemUriByItemType(this._filteredItems[this._selectedItemIndex]));
    this._close();
  });

  _setFilter = action((e: React.FormEvent<HTMLInputElement>) => {
    e.preventDefault();
    e.stopPropagation();
    this._filter = e.currentTarget.value;
  });

  @computed
  get _highlightKey(): string {
    return !this._filteredItems ||
      this._filteredItems.length === 0 ||
      this._selectedItemIndex > this._filteredItems.length - 1
      ? ""
      : this._filteredItems[this._selectedItemIndex].highlightKey;
  }

  @computed
  get _filteredItems(): Array<AutocompleteValue> {
    if (!this._items) {
      return null;
    }
    if (!this._filter) {
      return this._items;
    } else {
      const byValue = fuzzyFilter(this._items, ["displayValue"], this._filter);
      if (!byValue.length && this._items.length && this._filter.length > 2) {
        return isUUID(this._filter)
          ? fuzzyFilter(this._items, ["id"], this._filter)
          : this._items.filter((x) => x.idParts && x.idParts.findIndex((y) => y.startsWith(this._filter)) > -1);
      } else {
        return byValue;
      }
    }
  }

  render() {
    return (
      <Dialog open={this.context.api.omniSearch} className="omniSearch" onClose={this._close}>
        <React.Fragment>
          <div className="content">
            <span>
              <input autoFocus onChange={this._setFilter} value={this._filter} />
              {!this._filteredItems && <span className="spinner" />}
            </span>
            {this._filteredItems && <OmniItems items={this._filteredItems} selected={this._highlightKey} />}
          </div>
          <span className="help">
            <span>
              <strong>&uarr;</strong> <strong>&darr;</strong>
              <span>&nbsp; to navigate</span>
            </span>
            <span>
              <strong aria-label="Return">&crarr;</strong>
              <span>&nbsp; to select</span>
            </span>
            <span>
              <strong aria-label="Escape">esc</strong>
              <span>&nbsp; to dismiss</span>
            </span>
          </span>
        </React.Fragment>
      </Dialog>
    );
  }
}

function OmniSearchWrapper(props: any) {
  const { impersonate } = useLoginStateProvider();
  return <OmniSearch {...props} impersonate={impersonate} />;
}

export default withRouter(OmniSearchWrapper);
